import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './AutoReveal.css';

const TARGET_SELECTOR = [
  'section:not([class*="hero"])',
  '.privacy-trust-strip',
  '.terms-trust-strip',
  '.contact-grid > *',
  '.contact-map-section',
  '.privacy-overview',
  '.terms-overview',
  '.terms-agreement-callout',
  '.privacy-acc-item',
  '.terms-acc-item',
  '.legal-closing',
  '.services-vas-cell',
  '.process-roadmap-card',
  '.about-meet-team-card',
  '.about-why-card',
  '.about-why-intro-card',
  '.about-team-works-card',
  '.about-founder-top',
  '.about-founder-mid-grid',
  '.about-founder-card',
  '.about-founder-stat',
  '.about-founder-stats',
  '.about-split-copy',
  '.about-split-media',
  '.about-exp-visual',
  '.about-exp-copy',
  '.about-value-copy',
  '.about-value-visual',
  '.uc-icons',
  '.uc-badge',
  '.uc-title',
  '.uc-desc',
  '.uc-progress',
  '.uc-cards',
  '.uc-actions',
  '.uc-dots',
].join(',');

/**
 * Adds scroll-triggered reveal animations to common page blocks on every route.
 * Home keeps its own Framer Motion reveals, so it is skipped here.
 */
export default function AutoReveal({ rootRef }) {
  const { pathname } = useLocation();

  useEffect(() => {
    const root = rootRef?.current;
    if (!root) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;

    // Home already uses dedicated Reveal / motion components
    if (pathname === '/') return undefined;

    const heroes = [...root.querySelectorAll('section, header')].filter(
      (el) => /hero/i.test(el.className || '') && !el.classList.contains('js-hero-in')
    );

    heroes.forEach((el) => {
      el.classList.add('js-hero-in');
    });

    const nodes = [...root.querySelectorAll(TARGET_SELECTOR)].filter((el) => {
      if (/hero/i.test(el.className || '')) return false;
      return !el.classList.contains('js-reveal');
    });

    nodes.forEach((el, i) => {
      el.classList.add('js-reveal');
      el.style.setProperty('--reveal-delay', `${Math.min(i % 6, 5) * 0.06}s`);
    });

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.14, rootMargin: '0px 0px -6% 0px' }
    );

    nodes.forEach((el) => io.observe(el));

    requestAnimationFrame(() => {
      nodes.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.92 && rect.bottom > 40) {
          el.classList.add('is-visible');
          io.unobserve(el);
        }
      });
    });

    return () => {
      io.disconnect();
      heroes.forEach((el) => el.classList.remove('js-hero-in'));
      nodes.forEach((el) => {
        el.classList.remove('js-reveal', 'is-visible');
        el.style.removeProperty('--reveal-delay');
      });
    };
  }, [pathname, rootRef]);

  return null;
}
